import { useEffect, Dispatch, SetStateAction } from 'react'
import styled from '@emotion/styled'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import { AccountFieldManager, AuthFieldType } from '../components'
import { t } from '@lingui/core/macro'
import { AccountType } from '../types'

interface SnowflakeProps {
  newConnection: boolean
  updatedAccount: AccountType
  setRequiredFieldMissing: Dispatch<SetStateAction<boolean>>
  setUpdatedAccount: Dispatch<SetStateAction<AccountType>>
}

const FlexRadioInline = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  margin-bottom: 10px;
`

export const Snowflake = ({
  updatedAccount,
  setUpdatedAccount,
  setRequiredFieldMissing,
  newConnection,
}: SnowflakeProps) => {
  const SnowflakeFields: AuthFieldType[] = [
    {
      fieldValue: updatedAccount.metadata.username,
      fieldKey: 'username',
      fieldLabel: t`Username`,
      errorText: t`*Required`,
      hasError: !updatedAccount.metadata.username,
    },
    {
      fieldValue: updatedAccount.metadata.password,
      fieldKey: 'password',
      fieldLabel: t`Password`,
      isSecret: true,
      errorText: t`*Required`,
      hasError: !updatedAccount.metadata.password,
      description: t`The Snowflake password is securely stored and never surfaced to the user. Entering a new value will override the existing password.`,
    },
    {
      fieldValue: updatedAccount.metadata.role,
      fieldKey: 'role',
      fieldLabel: t`Role`,
    },
    {
      fieldValue: updatedAccount.metadata.warehouse,
      fieldKey: 'warehouse',
      fieldLabel: t`Warehouse`,
    },
    {
      fieldValue: updatedAccount.metadata.database,
      fieldKey: 'database',
      fieldLabel: t`Database`,
    },
    {
      fieldValue: updatedAccount.metadata.schema,
      fieldKey: 'schema',
      fieldLabel: t`Schema`,
    },
    {
      fieldValue: updatedAccount.metadata.url,
      fieldKey: 'url',
      fieldLabel: t`Url`,
      errorText: t`Invalid Url`,
      hasError: !updatedAccount.metadata.url?.match(/^https:\/\/([\w-]+\.){2,}(\w+)$/g),
    },
  ]

  const updateMetadataAuthType = (value: string) => {
    setUpdatedAccount(prevState => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        username: '',
        password: '',
        role: '',
        warehouse: '',
        database: '',
        schema: '',
        url: '',
        auth_type: value,
      },
    }))
  }

  const setDefaultValues = () => {
    setUpdatedAccount(prevState => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        username: '',
        password: '',
        role: 'CLARAVINE_ROLE',
        warehouse: 'CLARAVINE_WAREHOUSE',
        database: 'CLARAVINE_DATABASE',
        schema: 'CLARAVINE_SCHEMA',
        url: '',
        auth_type: 'fields',
      },
    }))
  }

  useEffect(() => {
    if (!newConnection && updatedAccount.metadata.auth_type) {
      updateMetadataAuthType(updatedAccount.metadata.auth_type)
    } else {
      setDefaultValues()
    }
  }, [newConnection])

  return (
    <div>
      <FlexRadioInline
        value={updatedAccount.metadata.auth_type ?? 'fields'}
        onChange={({ target: { value } }) => {
          updateMetadataAuthType(value)
        }}
      >
        <FormControlLabel value="fields" control={<Radio />} label={t`Username/Password`} />
      </FlexRadioInline>
      <AccountFieldManager
        newConnection={newConnection}
        updatedAccount={updatedAccount}
        setUpdatedAccount={setUpdatedAccount}
        accountFields={SnowflakeFields}
        setRequiredFieldsMissing={setRequiredFieldMissing}
      />
    </div>
  )
}
